import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '../design-system/atoms/Button';
import { Card } from '../design-system/atoms/Card';
import { C, F } from '../design-system/tokens';
import { requestPasswordReset } from '../domains/auth/api';
import { useAuthStore } from '../domains/auth/authStore';
import { isSupabaseConfigured } from '../domains/auth/supabaseClient';
import { getDefaultAppRoute } from '../domains/auth/companyRole';

// 1.30.0 / P0-4 — Запрос письма для сброса пароля.
//
// Поведение по защите от user enumeration: и при успехе, и при «email не
// найден» показываем одинаковый нейтральный текст «Если такой email
// зарегистрирован, мы отправили письмо». Supabase Auth сам не возвращает
// 404 на несуществующий email в resetPasswordForEmail, так что мы просто
// рендерим один и тот же success-экран на любой ответ без ошибки.
export function ForgotPassword() {
  const user = useAuthStore((state) => state.user);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Если юзер уже залогинен — на этой странице ему делать нечего, уводим
  // в его рабочую область. (Чисто гигиена UX, не безопасность.)
  if (user) {
    return <Navigate to={getDefaultAppRoute(user, user.company_membership_id)} replace />;
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await requestPasswordReset(email);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отправить письмо');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
        background: C.bg,
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          padding: '24px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        <Brand
          subtitle={submitted ? 'Проверьте почту' : 'Восстановление пароля'}
        />

        {!isSupabaseConfigured ? (
          <UnavailableNotice />
        ) : submitted ? (
          <SubmittedNotice email={email} />
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 12 }}>
            <p style={{ color: C.sub, fontSize: 13, lineHeight: 1.5, margin: 0 }}>
              Укажите email, на который зарегистрирована учётная запись. Если
              такой email есть в системе, мы отправим письмо со ссылкой
              для смены пароля.
            </p>

            <label style={{ display: 'grid', gap: 6 }}>
              <span style={fieldLabelStyle}>Email</span>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@company.com"
                autoComplete="email"
                type="email"
                required
                style={inputStyle}
              />
            </label>

            {error ? <ErrorBox text={error} /> : null}

            <Button
              type="submit"
              fw
              sz="lg"
              loading={loading}
              ch="ОТПРАВИТЬ ПИСЬМО"
            />
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: 4 }}>
          <Link
            to="/login"
            style={{
              color: C.sub,
              fontSize: 12,
              textDecoration: 'none',
              fontFamily: F.head,
              fontWeight: 600,
              letterSpacing: '.4px',
            }}
          >
            ← Назад ко входу
          </Link>
        </div>
      </Card>
    </div>
  );
}

function Brand({ subtitle }: { subtitle: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 18,
          background: `linear-gradient(145deg, ${C.acc}cc, ${C.acc})`,
          boxShadow: `0 8px 28px ${C.acc}55`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
          <path
            d="M17 3L30 10.5V23.5L17 31L4 23.5V10.5L17 3Z"
            stroke="rgba(255,255,255,0.9)"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M12 12h10M12 17h7M12 22h4"
            stroke="white"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <div>
        <div
          style={{
            fontFamily: F.head,
            fontWeight: 800,
            fontSize: 26,
            color: C.txt,
            letterSpacing: '-.3px',
          }}
        >
          Кворум
        </div>
        <div style={{ color: C.sub, marginTop: 4, fontSize: 13, lineHeight: 1.5 }}>
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function SubmittedNotice({ email }: { email: string }) {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div
        style={{
          padding: '14px 14px',
          borderRadius: 10,
          background: C.grS,
          border: `1px solid ${C.gr}33`,
          color: C.txt,
          fontSize: 13,
          lineHeight: 1.55,
        }}
      >
        Если email <strong style={{ color: C.txt }}>{email}</strong> зарегистрирован
        в Кворум, мы отправили на него письмо со ссылкой для смены пароля.
        Откройте его на этом же устройстве и перейдите по кнопке.
      </div>
      <div style={{ color: C.sub, fontSize: 11, lineHeight: 1.55 }}>
        Письмо может прийти в течение нескольких минут. Проверьте папку «Спам»,
        если не видите его в основном ящике.
      </div>
    </div>
  );
}

function UnavailableNotice() {
  return (
    <div
      style={{
        padding: '14px',
        borderRadius: 10,
        background: C.amS,
        border: `1px solid ${C.am}33`,
        color: C.txt,
        fontSize: 13,
        lineHeight: 1.55,
      }}
    >
      Восстановление пароля недоступно в локальной демо-сборке: для отправки
      письма нужно настроенное подключение к Supabase.
    </div>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div
      style={{
        padding: '9px 12px',
        borderRadius: 8,
        background: C.reS,
        border: `1px solid ${C.re}44`,
        color: C.re,
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      {text}
    </div>
  );
}

const fieldLabelStyle: React.CSSProperties = {
  color: C.sub,
  fontSize: 11,
  fontFamily: F.head,
  fontWeight: 700,
  letterSpacing: '.4px',
  textTransform: 'uppercase',
};

const inputStyle: React.CSSProperties = {
  background: C.surf,
  border: `1px solid ${C.b}`,
  color: C.txt,
  borderRadius: 10,
  padding: '12px 14px',
  outline: 'none',
  fontSize: 14,
  fontFamily: F.body,
};
